import React, { useState } from "react";
import { motion } from "framer-motion";

const ShoppingGuiltBlogPost = ({ goToInput, theme, setCurrentView }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Theme-based accent styling
  const accentStyles = {
    mint: "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20",
    pink: "border-pink-500 bg-pink-50 dark:bg-pink-900/20",
    lavender: "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
  };

  // Blog sections for collapsible content
  const blogSections = [
    {
      id: "intro",
      title: "Why Do We Feel Guilty After Shopping?",
      icon: "🤔",
      content: (
        <div className="space-y-4">
          <p>
            We've all been there: you're scrolling through your favorite online store, 
            and suddenly you're adding items to your cart, checking out, and soon 
            packages are arriving at your door. Initially, there's excitement, but 
            then comes that nagging feeling – <strong>shopping guilt</strong>.
          </p>
          <p>
            This post explores the psychology behind shopping guilt, why we experience 
            it, and most importantly, practical strategies to overcome it and develop 
            healthier spending habits.
          </p>
          <div className={`p-4 rounded-xl ${accentStyles[theme]} mb-4`}>
            <p className="font-medium">
              Shopping guilt is an emotional response that occurs when our purchases 
              don't align with our values, budget, or needs. It's the uncomfortable 
              feeling that follows when we question if we've made the right decision.
            </p>
          </div>
        </div>
      )
    },
    {
      id: "triggers",
      title: "Common Shopping Guilt Triggers",
      icon: "💸",
      content: (
        <div className="space-y-4">
          <p>
            Understanding what triggers your shopping guilt is the first step to addressing it. 
            Here are the most common reasons people experience regret after making purchases:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <span className="text-xl mr-3">💰</span>
                <div>
                  <h4 className="font-semibold dark:text-white">Financial Strain</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Spending beyond your budget or using funds allocated for other purposes.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <span className="text-xl mr-3">😢</span>
                <div>
                  <h4 className="font-semibold dark:text-white">Emotional Shopping</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Buying things when sad, bored, or stressed to temporarily feel better.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <span className="text-xl mr-3">👀</span>
                <div>
                  <h4 className="font-semibold dark:text-white">FOMO Purchases</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Buying due to fear of missing out on deals, trends, or limited editions.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <span className="text-xl mr-3">🧠</span>
                <div>
                  <h4 className="font-semibold dark:text-white">Value Misalignment</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Purchases that don't align with your personal values or priorities.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 italic mt-4">
            According to a study by CreditCards.com, nearly 80% of shoppers experience some form of buyer's remorse, with impulse purchases being the most common trigger.
          </p>
        </div>
      )
    },
    {
      id: "psychology",
      title: "The Psychology Behind Shopping Guilt",
      icon: "🧠",
      content: (
        <div className="space-y-4">
          <p>
            Shopping guilt isn't just about money—it's a complex psychological response 
            involving several cognitive and emotional factors:
          </p>
          
          <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 my-4">
            <h4 className="font-medium dark:text-white">Cognitive Dissonance</h4>
            <p className="text-gray-600 dark:text-gray-300">
              The mental discomfort that occurs when our actions (spending) don't align with our beliefs 
              (financial responsibility). This psychological tension creates the feeling we identify as guilt.
            </p>
          </div>
          
          <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 my-4">
            <h4 className="font-medium dark:text-white">Hedonic Adaptation</h4>
            <p className="text-gray-600 dark:text-gray-300">
              The tendency to quickly return to a baseline level of happiness after purchasing something new. 
              That initial joy fades, but the financial impact remains, triggering guilt.
            </p>
          </div>
          
          <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 my-4">
            <h4 className="font-medium dark:text-white">Loss Aversion</h4>
            <p className="text-gray-600 dark:text-gray-300">
              We feel the pain of loss (money spent) more strongly than the pleasure of gain (item acquired). 
              This imbalance can lead to post-purchase regret.
            </p>
          </div>
          
          <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 my-4">
            <h4 className="font-medium dark:text-white">Social Comparison</h4>
            <p className="text-gray-600 dark:text-gray-300">
              Comparing our purchases to others' possessions or perceived financial habits can trigger guilt, 
              especially in the age of social media.
            </p>
          </div>
          
          <div className={`p-4 rounded-xl ${accentStyles[theme]} mt-6`}>
            <div className="flex items-start">
              <div className="text-xl mr-3">💡</div>
              <div>
                <p className="font-medium">Understanding Insight:</p>
                <p className="text-sm">
                  Shopping guilt isn't just a "you" problem—it's a normal psychological response that has been intensified 
                  by modern consumer culture, advertising, and the ease of online shopping. Recognizing these external 
                  factors can help reduce self-blame while working on healthier habits.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "strategies",
      title: "7 Practical Strategies to Overcome Shopping Guilt",
      icon: "🛠️",
      content: (
        <div className="space-y-4">
          <p>
            Rather than letting guilt consume you after purchases, try these evidence-based strategies 
            to develop healthier shopping habits and reduce buyer's remorse:
          </p>
          
          <div className="space-y-6 mt-4">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Implement the 24-Hour Rule</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    For non-essential purchases, wait 24 hours before buying. This cooling-off period helps distinguish 
                    between genuine needs and impulsive wants.
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-2 italic">
                    "I put items in my cart but close the tab. If I'm still thinking about it tomorrow, then I'll reconsider."
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Create a Dedicated "Fun Money" Budget</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Allocate a specific amount each month for guilt-free discretionary spending. When you know 
                    you're spending within your planned budget, guilt decreases significantly.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Track Your Emotional Triggers</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Use Guilt Gauge to log how you're feeling when shopping. Identifying patterns can help you 
                    develop alternative coping mechanisms for emotional states that trigger spending.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Practice Mindful Consumption</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Before purchasing, ask: "Does this align with my values? Will I still want this in a month? 
                    Does it serve a purpose in my life?" These questions promote intentional spending.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Calculate Cost-Per-Use</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Divide the cost of an item by how many times you'll realistically use it. This perspective 
                    shift helps justify higher-quality items that last longer and provide better value.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">6</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Implement the One-In-One-Out Rule</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    For categories where you tend to accumulate (clothing, gadgets, home decor), commit to 
                    removing one item for each new one you bring in. This maintains balance and encourages thoughtful acquisitions.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <div className="flex items-start">
                <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 h-7 w-7 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">7</div>
                <div>
                  <h4 className="font-semibold dark:text-white">Practice Self-Compassion</h4>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">
                    If you do experience shopping guilt, treat yourself with kindness. Acknowledge the feeling, 
                    learn from the experience, and move forward with better awareness rather than harsh self-judgment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "special-situations",
      title: "Special Situations: Holiday Shopping, Big Purchases & More",
      icon: "🎁",
      content: (
        <div className="space-y-4">
          <p>
            Certain shopping scenarios bring their own unique challenges and guilt triggers. 
            Here's how to navigate them:
          </p>
          
          <div className="grid grid-cols-1 gap-4 mt-4">
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <h4 className="font-semibold dark:text-white flex items-center">
                <span className="text-xl mr-2">🎄</span> Holiday Shopping
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                The pressure of gift-giving, sales, and festive atmosphere can lead to significant overspending.
              </p>
              <div className="mt-2 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Strategy:</strong> Create a holiday budget in advance, with specific amounts for each person. 
                  Consider homemade gifts, shared experiences, or organizing gift exchanges to reduce the number of purchases.
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <h4 className="font-semibold dark:text-white flex items-center">
                <span className="text-xl mr-2">💎</span> Luxury Purchases
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                High-ticket items naturally trigger greater guilt due to their significant financial impact.
              </p>
              <div className="mt-2 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Strategy:</strong> For major purchases, create a specific savings goal and timeline. 
                  When you've saved the full amount intentionally over time, the purchase becomes a planned reward 
                  rather than an impulse that triggers guilt.
                </p>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm">
              <h4 className="font-semibold dark:text-white flex items-center">
                <span className="text-xl mr-2">📱</span> Subscription Services
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mt-1">
                The "small" monthly fees that accumulate and are often forgotten can lead to subscription guilt.
              </p>
              <div className="mt-2 pl-4 border-l-2 border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Strategy:</strong> Conduct a quarterly subscription audit. For each service, ask: 
                  "Have I used this in the last month? Does it still bring value? Could I share this with someone?" 
                  Cancel anything that doesn't pass this test.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "when-to-seek-help",
      title: "When Shopping Guilt Signals a Bigger Issue",
      icon: "⚕️",
      content: (
        <div className="space-y-4">
          <p>
            While occasional shopping guilt is normal, persistent and intense guilt might signal a 
            more serious concern, such as compulsive buying disorder or shopping addiction.
          </p>
          
          <div className={`p-4 rounded-xl ${accentStyles[theme]} mb-4`}>
            <div className="flex items-start">
              <div className="text-xl mr-3">⚠️</div>
              <div>
                <p className="font-medium">Warning Signs:</p>
                <ul className="list-disc list-inside text-sm space-y-1 mt-1">
                  <li>Shopping that consistently interferes with your financial obligations</li>
                  <li>Hiding purchases from loved ones</li>
                  <li>Feeling a rush or high while shopping, followed by intense guilt</li>
                  <li>Experiencing anxiety or distress when unable to shop</li>
                  <li>Accumulating unused items with tags still attached</li>
                </ul>
              </div>
            </div>
          </div>
          
          <p>
            If these signs sound familiar, consider reaching out to a mental health professional 
            specializing in behavioral addictions or financial therapy. There's no shame in seeking 
            help for shopping behaviors that have become harmful.
          </p>
          
          <div className="bg-white dark:bg-gray-700 rounded-xl p-4 shadow-sm mt-4">
            <h4 className="font-semibold dark:text-white">Resources for Support</h4>
            <ul className="list-disc list-inside text-sm space-y-1 mt-2 text-gray-600 dark:text-gray-300">
              <li>Debtors Anonymous: Offers support groups for those struggling with compulsive spending</li>
              <li>Financial therapists: Professionals who address the emotional aspects of money behaviors</li>
              <li>Cognitive Behavioral Therapy (CBT): An effective approach for addressing compulsive shopping</li>
              <li>Take our <a href="#" className="text-blue-600 dark:text-blue-400 hover:underline" onClick={(e) => {e.preventDefault(); setCurrentView("shopaholic-test")}}>Shopping Addiction Assessment</a> for personalized insights</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: "conclusion",
      title: "From Guilt to Mindful Shopping: Your Journey Starts Here",
      icon: "🌱",
      content: (
        <div className="space-y-4">
          <p>
            Shopping guilt doesn't have to be a permanent part of your financial life. By understanding 
            its psychological roots and implementing practical strategies, you can transform your 
            relationship with spending and enjoy more mindful, intentional purchases.
          </p>
          
          <p>
            Remember, the goal isn't to never treat yourself or only buy necessities—it's to create 
            a healthy balance where your spending aligns with your values and financial goals.
          </p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-5 mt-4">
            <h4 className="font-semibold text-center mb-3 dark:text-white">Ready to Transform Your Shopping Habits?</h4>
            <p className="text-center text-gray-700 dark:text-gray-300 mb-4">
              Start tracking your purchases with Guilt Gauge to understand your personal spending patterns, 
              identify emotional triggers, and develop a healthier relationship with shopping.
            </p>
            <div className="flex justify-center">
              <motion.button
                onClick={goToInput}
                className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Calculate Your Guilt Score
              </motion.button>
            </div>
          </div>
        </div>
      )
    }
  ];

  // Toggle section expansion
  const toggleSection = (id) => {
    if (expandedSection === id) {
      setExpandedSection(null); // collapse if already expanded
    } else {
      setExpandedSection(id); // expand the clicked section
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-3 dark:text-white">Why Do We Feel Guilty After Shopping?</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Understand the psychology behind shopping guilt and discover 7 evidence-based strategies to overcome buyer's remorse.
        </p>
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mt-3">
          <span className="mr-4">Updated May 2023</span>
          <span className="mr-4">10 min read</span>
          <span>Shopping Psychology</span>
        </div>
      </div>

      {/* Table of Contents */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3 dark:text-white">What You'll Learn:</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {blogSections.map((section) => (
            <motion.button
              key={section.id}
              onClick={() => toggleSection(section.id)}
              className="flex items-center text-left p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              whileHover={{ x: 5 }}
            >
              <span className="text-xl mr-3">{section.icon}</span>
              <span className="text-gray-700 dark:text-gray-300 text-sm">{section.title}</span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Blog Content Sections */}
      <div className="space-y-6">
        {blogSections.map((section) => (
          <div key={section.id} className="border-b border-gray-200 dark:border-gray-700 pb-6 last:border-0">
            <motion.button
              onClick={() => toggleSection(section.id)}
              className="flex items-center justify-between w-full text-left py-3"
              whileHover={{ scale: 1.01 }}
            >
              <div className="flex items-center">
                <span className="text-2xl mr-3">{section.icon}</span>
                <h2 className="text-xl font-bold dark:text-white">{section.title}</h2>
              </div>
              <span className="text-xl">
                {expandedSection === section.id ? "−" : "+"}
              </span>
            </motion.button>
            
            <motion.div
              initial={false}
              animate={{
                height: expandedSection === section.id ? "auto" : 0,
                opacity: expandedSection === section.id ? 1 : 0,
              }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="py-4">
                {section.content}
              </div>
            </motion.div>
          </div>
        ))}
      </div>

      {/* CTA Footer */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Ready to Shop Without the Guilt?</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Start tracking your purchases and develop more mindful shopping habits today.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Calculate Your First Guilt Score
        </motion.button>
      </div>
    </motion.div>
  );
};

export default ShoppingGuiltBlogPost;
